import { useCallback, useEffect, useState } from 'react';
import { cajaService, configuracionService, metodosPagoService, turnosService } from '../../services/api';
import { useToast } from '../../components/ui/Toast';
import ModalLlaveAcceso from '../../components/ModalLlaveAcceso';
import { btnPrimario, btnSecundario, pesos } from '../../components/ui/estilos';
import Dialogo from '../../components/ui/Dialogo';
import { DialogoCajon } from '../cuentas/DialogosCuenta';
import { DialogoCierre, DialogoMovimiento, NuevoMovimiento } from './DialogosTurno';

const n = (v: any) => parseFloat(v || 0) || 0;
const cuadro: React.CSSProperties = { ...btnSecundario, width: 52, height: 52, padding: 0, fontSize: 24, borderRadius: 12 };

// Qué acción espera la contraseña (llave de acceso con su permiso).
export type Llave =
  | { tipo: 'abrir'; cajon: boolean }
  | { tipo: 'cerrar'; cajon: boolean }
  | { tipo: 'movimiento'; mov: NuevoMovimiento }
  | { tipo: 'propina'; p: any }
  | { tipo: 'cancelar_mov'; mv: any };

// Estado y acciones del turno de caja: lo usan Inicio (tarjeta) y Turno (detalle).
// `elementos` son los diálogos y la contraseña; cada pantalla los pinta una vez.
export function useCajaTurno() {
  const [toast, say] = useToast();
  const [cargando, setCargando] = useState(true);
  const [turno, setTurno] = useState<any>(null);
  const [resumen, setResumen] = useState<any>(null);
  const [historial, setHistorial] = useState<any[]>([]);
  const [metodos, setMetodos] = useState<any[]>([]);
  const [ultimoTicket, setUltimoTicket] = useState<any>(null);
  const [fondoTxt, setFondoTxt] = useState('');
  const fondo = parseFloat(fondoTxt) || 0;
  const [verHistorial, setVerHistorial] = useState(false);
  const [dialogo, setDialogo] = useState<null | 'abrir' | 'abrir_conteo' | 'abrir_confirmar' | 'confirmar_cierre' | 'entrada' | 'salida' | 'cierre' | 'cajon'>(null);
  const [llave, setLlave] = useState<Llave | null>(null);
  const [cierre, setCierre] = useState<{ declaracion: Record<string, number>; notas: string } | null>(null);

  const cargar = useCallback(async () => {
    try {
      const [a, h] = await Promise.all([turnosService.getActivo(), turnosService.getAll()]);
      setTurno(a.data.turno); setResumen(a.data.resumen); setHistorial(h.data);
      if (a.data.turno) cajaService.getHistorial({ limit: 1 }).then(r => setUltimoTicket(r.data.tickets?.[0] ?? null)).catch(() => {});
    } catch { say('No se pudo cargar el turno.'); }
    finally { setCargando(false); }
  }, [say]);

  useEffect(() => { cargar(); metodosPagoService.getAll().then(r => setMetodos(r.data)).catch(() => {}); }, [cargar]);

  const activos = (metodos.filter(m => m.activo).length ? metodos.filter(m => m.activo) : [{ clave: 'efectivo', nombre: 'Efectivo' }]);
  const cobrado = (clave: string) => n(resumen?.cobrado_por_metodo?.find((v: any) => v.metodo === clave)?.total);
  const efectivoEsperado = turno ? n(turno.fondo_inicial) + (resumen?.total_ventas_efectivo || 0) + (resumen?.total_depositos || 0) - (resumen?.total_retiros || 0) : 0;

  /* ─── Acciones que corren después de la contraseña ─── */
  const ejecutar = async (l: Llave) => {
    if (l.tipo === 'abrir') {
      // La contraseña autoriza la apertura; el turno se crea hasta confirmar el fondo contado.
      if (l.cajon) configuracionService.abrirCajon().catch(() => say('No se pudo abrir el cajón.'));
      setFondoTxt('');
      setDialogo('abrir_conteo');
      return;
    }
    if (l.tipo === 'cerrar') {
      // Igual que la apertura: la contraseña autoriza, se cuenta y se confirma antes de cerrar.
      if (l.cajon) configuracionService.abrirCajon().catch(() => say('No se pudo abrir el cajón.'));
      setDialogo('cierre');
      return;
    }
    try {
      if (l.tipo === 'movimiento') {
        await turnosService.registrarMovimiento(turno.id, l.mov);
        configuracionService.abrirCajon().catch(() => {});
        say(`${l.mov.tipo === 'deposito' ? 'Entrada' : 'Salida'} de ${pesos(l.mov.monto)} registrada.`);
      }
      if (l.tipo === 'propina') {
        // mesero_id: descuenta del saldo acumulado del mesero, no del texto del motivo.
        await turnosService.registrarMovimiento(turno.id, { tipo: 'retiro', monto: l.p.monto, motivo: `Propina mesero ${l.p.mesero}`, mesero_id: l.p.mesero_id });
        configuracionService.abrirCajon().catch(() => {});
        say(`Propina de ${l.p.mesero} entregada.`);
      }
      if (l.tipo === 'cancelar_mov') {
        await turnosService.cancelarMovimiento(turno.id, l.mv.id);
        say('Movimiento cancelado.');
      }
    } catch (e: any) {
      say(e.response?.data?.error || 'No se pudo completar la acción.');
    } finally { cargar(); }
  };

  const difCierre = (declaracion: Record<string, number>) =>
    Object.values(declaracion).reduce((s, v) => s + v, 0) - filasCierre().reduce((s, f) => s + f.esperado, 0);
  const textoDif = (dif: number) => Math.abs(dif) < .005 ? 'exacta' : `${dif > 0 ? '+' : '−'}${pesos(Math.abs(dif))}`;

  const cerrarTurno = async () => {
    if (!cierre) return;
    const { declaracion, notas } = cierre;
    setCierre(null);
    setDialogo(null);
    try {
      await turnosService.cerrar(turno.id, { fondo_final: declaracion.efectivo || 0, notas_cierre: notas, declaracion_cierre: declaracion });
      await imprimirCorte(declaracion);
      say(`Turno cerrado. Diferencia: ${textoDif(difCierre(declaracion))}.`);
    } catch (e: any) {
      say(e.response?.data?.error || 'No se pudo cerrar el turno.');
    } finally { cargar(); }
  };

  const abrirTurno = async () => {
    setDialogo(null);
    try {
      await turnosService.abrir({ fondo_inicial: fondo });
      say(`Turno abierto con ${pesos(fondo)}.`);
    } catch (e: any) {
      say(e.response?.data?.error || 'No se pudo abrir el turno.');
    } finally { cargar(); }
  };

  // El corte se imprime directo a la térmica; si falla la impresora no se bloquea el cierre.
  const imprimirCorte = async (declaracion: Record<string, number>) => {
    if (!resumen) return;
    try {
      await turnosService.imprimirCorte(turno.id, {
        fondo_inicial: turno.fondo_inicial, cajero: turno.usuario_apertura, metodos_pago: metodos,
        ventas_por_metodo: resumen.ventas_por_metodo, cobrado_por_metodo: resumen.cobrado_por_metodo,
        comisiones: resumen.comisiones, propinas_por_metodo: resumen.propinas_por_metodo,
        ventas_por_categoria: resumen.ventas_por_categoria, total_ventas: resumen.total_ventas,
        total_ventas_efectivo: resumen.total_ventas_efectivo, total_propinas: resumen.total_propinas,
        total_depositos: resumen.total_depositos, total_retiros: resumen.total_retiros,
        movimientos_por_categoria: resumen.movimientos_por_categoria, cuentas_normales: resumen.cuentas_normales,
        cuentas_canceladas: resumen.cuentas_canceladas, cuentas_con_descuento: resumen.cuentas_con_descuento,
        cuenta_promedio: resumen.cuenta_promedio, consumo_promedio: resumen.consumo_promedio,
        total_comensales: resumen.total_comensales, propina_cargos: resumen.propina_cargos, declaracion,
      });
    } catch { /* impresora apagada: el corte queda en el historial */ }
  };

  const imprimirPropinas = () => {
    const porMesero: Record<string, number> = {};
    (resumen?.propinas_pendientes || []).forEach((p: any) => { const v = p.a_entregar ?? p.pendiente; if (v > .005) porMesero[p.mesero] = v; });
    turnosService.imprimirPropinas({ propinas: porMesero, cajero: turno?.usuario_apertura })
      .then(() => say('Propinas enviadas a la impresora.')).catch(() => say('No se pudo imprimir; revisa la impresora.'));
  };

  // Efectivo: fondo + ventas + entradas − salidas. Los demás: lo cobrado (con su propina).
  const filasCierre = () => activos.map((m: any) => ({ clave: m.clave, label: m.nombre, esperado: m.clave === 'efectivo' ? efectivoEsperado : cobrado(m.clave) }));


  const elementos = (
    <>
      {(dialogo === 'entrada' || dialogo === 'salida') && (
        <DialogoMovimiento tipo={dialogo === 'entrada' ? 'deposito' : 'retiro'} enCaja={efectivoEsperado} onCerrar={() => setDialogo(null)}
          onContinuar={mov => { setDialogo(null); setLlave({ tipo: 'movimiento', mov }); }} />
      )}
      {dialogo === 'cierre' && <DialogoCierre filas={filasCierre()} onCerrar={() => setDialogo(null)}
        onConfirmar={(declaracion, notas) => setCierre({ declaracion, notas })} />}
      {/* Encima del conteo: "Corregir" regresa sin perder lo capturado. */}
      {dialogo === 'cierre' && cierre && (
        <Dialogo titulo="¿Cerrar turno?" onCerrar={() => setCierre(null)} ancho={420}>
          <p style={{ margin: 0, color: 'var(--muted)' }}>Efectivo contado: <b>{pesos(cierre.declaracion.efectivo || 0)}</b> · diferencia <b>{textoDif(difCierre(cierre.declaracion))}</b>. Se imprime el corte.</p>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setCierre(null)} style={{ ...btnSecundario, flex: 1 }}>Corregir</button>
            <button autoFocus onClick={cerrarTurno} style={{ ...btnPrimario, flex: 1 }}>Sí, cerrar turno</button>
          </div>
        </Dialogo>
      )}
      {dialogo === 'cajon' && <DialogoCajon onCerrar={() => setDialogo(null)} />}
      {/* Apertura: ¿seguro? + ¿abrir cajón? → contraseña → contar fondo → confirmar. */}
      {dialogo === 'abrir' && (
        <Dialogo titulo="¿Seguro que quieres abrir turno?" onCerrar={() => setDialogo(null)} ancho={420}>
          <p style={{ margin: 0, color: 'var(--muted)' }}>Primero cuenta el efectivo con el que arrancas. ¿Abrimos el cajón para contar?</p>
          <button autoFocus onClick={() => { setDialogo(null); setLlave({ tipo: 'abrir', cajon: true }); }} style={btnPrimario}>Sí, abrir cajón y contar</button>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setDialogo(null)} style={{ ...btnSecundario, flex: 1 }}>No, cancelar</button>
            <button onClick={() => { setDialogo(null); setLlave({ tipo: 'abrir', cajon: false }); }} style={{ ...btnSecundario, flex: 1 }}>Contar sin abrir</button>
          </div>
        </Dialogo>
      )}
      {dialogo === 'abrir_conteo' && (
        <Dialogo titulo="¿Cuánto efectivo hay en caja?" onCerrar={() => setDialogo(null)} ancho={420}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <button onClick={() => setFondoTxt(String(Math.max(0, +(fondo - 100).toFixed(2))))} aria-label="Menos" style={cuadro}>−</button>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 32, fontWeight: 700 }}>
              $<input autoFocus value={fondoTxt} placeholder="0" inputMode="decimal" aria-label="Fondo inicial"
                onChange={e => setFondoTxt(e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1').replace(/(\.\d{2})\d+/, '$1'))}
                onKeyDown={e => e.key === 'Enter' && setDialogo('abrir_confirmar')}
                style={{ width: 150, textAlign: 'center', fontSize: 32, fontWeight: 700, border: 'none', borderBottom: '2px solid var(--line-strong)', background: 'transparent', color: 'var(--ink)' }} />
            </label>
            <button onClick={() => setFondoTxt(String(+(fondo + 100).toFixed(2)))} aria-label="Más" style={cuadro}>+</button>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setDialogo(null)} style={{ ...btnSecundario, flex: 1 }}>Cancelar</button>
            <button onClick={() => setDialogo('abrir_confirmar')} style={{ ...btnPrimario, flex: 1 }}>Continuar</button>
          </div>
        </Dialogo>
      )}
      {dialogo === 'abrir_confirmar' && (
        <Dialogo titulo={`¿Abrir turno con ${pesos(fondo)}?`} onCerrar={() => setDialogo('abrir_conteo')} ancho={420}>
          <p style={{ margin: 0, color: 'var(--muted)' }}>Ese será el fondo inicial de la caja.</p>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setDialogo('abrir_conteo')} style={{ ...btnSecundario, flex: 1 }}>Corregir</button>
            <button autoFocus onClick={abrirTurno} style={{ ...btnPrimario, flex: 1 }}>Sí, abrir turno</button>
          </div>
        </Dialogo>
      )}
      {dialogo === 'confirmar_cierre' && (
        <Dialogo titulo="¿Seguro que quieres cerrar turno?" onCerrar={() => setDialogo(null)} ancho={420}>
          <p style={{ margin: 0, color: 'var(--muted)' }}>Vas a contar el dinero en caja. ¿Abrimos el cajón para contar?</p>
          <button autoFocus onClick={() => { setDialogo(null); setLlave({ tipo: 'cerrar', cajon: true }); }} style={btnPrimario}>Sí, abrir cajón y contar</button>
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={() => setDialogo(null)} style={{ ...btnSecundario, flex: 1 }}>No, cancelar</button>
            <button onClick={() => { setDialogo(null); setLlave({ tipo: 'cerrar', cajon: false }); }} style={{ ...btnSecundario, flex: 1 }}>Contar sin abrir</button>
          </div>
        </Dialogo>
      )}

      {llave && (() => {
        const t: Record<Llave['tipo'], [string, string, string]> = {
          abrir:        ['puede_abrir_turno', 'Confirma tu contraseña', llave.tipo === 'abrir' && llave.cajon ? 'Escribe tu contraseña para abrir el cajón y contar el fondo.' : 'Escribe tu contraseña para abrir el turno.'],
          cerrar:       ['puede_cerrar_turno', 'Confirma tu contraseña', llave.tipo === 'cerrar' && llave.cajon ? 'Escribe tu contraseña para abrir el cajón y contar.' : 'Escribe tu contraseña para cerrar el turno.'],
          movimiento:   ['puede_depositar_retirar', 'Confirma tu contraseña', llave.tipo === 'movimiento' ? `Para registrar ${llave.mov.tipo === 'deposito' ? 'la entrada' : 'la salida'} de ${pesos(llave.mov.monto)} escribe tu contraseña.` : ''],
          propina:      ['puede_depositar_retirar', 'Entregar propina', llave.tipo === 'propina' ? `${llave.p.mesero} · ${pesos(llave.p.monto)} sale de caja.` : ''],
          cancelar_mov: ['puede_depositar_retirar', 'Cancelar movimiento', llave.tipo === 'cancelar_mov' ? `${llave.mv.motivo} · ${pesos(n(llave.mv.monto))}` : ''],
        };
        const [permiso, titulo, descripcion] = t[llave.tipo];
        return <ModalLlaveAcceso permiso={permiso} titulo={titulo} descripcion={descripcion}
          onCancel={() => setLlave(null)} onSuccess={() => { const l = llave; setLlave(null); ejecutar(l); }} />;
      })()}
      {toast}
    </>
  );

  return {
    cargando, turno, resumen, historial, ultimoTicket, fondo, verHistorial, setVerHistorial,
    activos, cobrado, efectivoEsperado, imprimirPropinas, setDialogo, setLlave, elementos,
  };
}
